import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { scheduleUndoableDelete } from "@/lib/undoDelete";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash, Factory } from "@phosphor-icons/react";

export default function Plants() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ kode: "", nama: "", aktif: true });

  const load = async () => {
    const { data } = await api.get("/plants");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ kode: "", nama: "", aktif: true });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ kode: p.kode, nama: p.nama, aktif: p.aktif });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/plants/${editing.id}`, form);
        toast.success("Plant diperbarui");
      } else {
        await api.post("/plants", form);
        toast.success("Plant ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDelete = (p) => {
    scheduleUndoableDelete({
      items, setItems, target: p,
      label: `Plant ${p.kode}`,
      apiCall: () => api.delete(`/plants/${p.id}`, { params: { hard: true } }),
    });
  };

  const filtered = items.filter((p) => !q || p.kode.toLowerCase().includes(q.toLowerCase()) || p.nama.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 md:p-8 max-w-5xl">
      <div className="flex flex-col md:flex-row md:items-end justify-between border-b border-border pb-6 mb-6 gap-4">
        <div>
          <div className="overline">Master Data</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
              <Factory size={22} weight="bold" />
            </span>
            Plant
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Kode plant dipakai untuk sufiks No SPB (contoh: <span className="font-mono font-bold">/BLW</span>).
          </p>
        </div>
        <Button onClick={openNew} className="rounded-lg font-bold uppercase tracking-wider" data-testid="add-plant-button">
          <Plus size={16} className="mr-2" /> Tambah Plant
        </Button>
      </div>

      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kode atau nama..." className="rounded-lg h-11 mb-4" data-testid="search-plant-input" />

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left">
            <tr>
              <th className="p-3 overline">Kode</th>
              <th className="p-3 overline">Nama Plant</th>
              <th className="p-3 overline">Status</th>
              <th className="p-3 overline">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-border hover:bg-secondary/50">
                <td className="p-3 font-mono font-bold">{p.kode}</td>
                <td className="p-3">{p.nama}</td>
                <td className="p-3">
                  <span className={`status-pill ${p.aktif ? "status-termuat" : "status-belum"}`}>
                    {p.aktif ? "AKTIF" : "NONAKTIF"}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openEdit(p)} data-testid={`edit-plant-${p.kode}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg text-destructive bg-destructive/5 border-destructive/40" onClick={() => onDelete(p)} data-testid={`delete-plant-${p.kode}`} title="Hapus permanen">
                      <Trash size={14} weight="fill" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Belum ada plant.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Plant" : "Tambah Plant"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="plant-form">
            <div>
              <Label className="overline block mb-2">Kode Plant *</Label>
              <Input
                value={form.kode}
                onChange={(e) => setForm((f) => ({ ...f, kode: e.target.value.toUpperCase() }))}
                required
                maxLength={10}
                placeholder="BLW"
                className="rounded-lg h-11 font-mono font-bold"
                data-testid="input-plant-kode"
              />
              <p className="text-xs text-muted-foreground mt-1">Singkat, huruf besar. Contoh: BLW, DUM, PDG.</p>
            </div>
            <div>
              <Label className="overline block mb-2">Nama Plant *</Label>
              <Input
                value={form.nama}
                onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))}
                required
                placeholder="79B4 BELAWAN TERMINAL"
                className="rounded-lg h-11"
                data-testid="input-plant-nama"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.aktif} onCheckedChange={(v) => setForm((f) => ({ ...f, aktif: v }))} data-testid="switch-plant-aktif" />
              <Label>Aktif</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" className="rounded-lg font-bold uppercase" data-testid="save-plant-button">Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
